/**
 * After the last week with period hours, earned / spent must not exist —
 * not as a flat cumulative line through the remaining / budget tail.
 */
export function stopPerformanceLines(rows) {
  if (!rows?.length) return rows || [];
  const eps = 0.0005;
  let lastEarned = -1;
  let lastSpent = -1;
  for (let i = 0; i < rows.length; i += 1) {
    if (Math.abs(Number(rows[i].earned_period) || 0) > eps) lastEarned = i;
    if (Math.abs(Number(rows[i].actual_period) || 0) > eps) lastSpent = i;
  }
  return rows.map((row, i) => {
    const next = { ...row };
    if (lastEarned >= 0 && i > lastEarned) {
      next.earned_period = 0;
      next.shop_earned_period = 0;
      next.field_earned_period = 0;
      next.earned_cumulative = null;
    }
    if (lastSpent >= 0 && i > lastSpent) {
      next.actual_period = 0;
      next.actual_cumulative = null;
    }
    return next;
  });
}

function roundIndex(n) {
  return Math.round(n * 1000) / 1000;
}

/**
 * Cumulative performance on the painted curve.
 * SPI = EV ÷ PV, CPI = EV ÷ AC, SV = EV − PV, CV = EV − AC.
 * Future weeks (earned or spent already nulled) stay null so the lines stop.
 */
export function attachEvmIndices(rows) {
  return (rows || []).map((row) => {
    const ev = row.earned_cumulative == null ? null : Number(row.earned_cumulative);
    const pv = row.budget_cumulative == null ? null : Number(row.budget_cumulative);
    const ac = row.actual_cumulative == null ? null : Number(row.actual_cumulative);
    const evOk = ev != null && Number.isFinite(ev);
    const pvOk = pv != null && Number.isFinite(pv);
    const acOk = ac != null && Number.isFinite(ac) && ac > 0;
    return {
      ...row,
      evm_spi: evOk && pvOk && pv > 0 ? roundIndex(ev / pv) : null,
      evm_cpi: evOk && acOk ? roundIndex(ev / ac) : null,
      evm_sv: evOk && pvOk ? ev - pv : null,
      evm_cv: evOk && acOk ? ev - ac : null
    };
  });
}

/** P6 proposal + P6 earned; Time Tracker spent when that feed is on. */
export function mergeTrackerWithP6Proposal(p6Series, trackerSeries) {
  const pMap = new Map((p6Series || []).map((r) => [String(r.date).slice(0, 10), r]));
  const tMap = new Map((trackerSeries || []).map((r) => [String(r.date).slice(0, 10), r]));
  const dates = [...new Set([...pMap.keys(), ...tMap.keys()])].filter(Boolean).sort();
  const eps = 0.0005;
  let lastSpentDate = '';
  let lastEarnedDate = '';
  for (const date of dates) {
    if (Math.abs(Number(tMap.get(date)?.actual_period) || 0) > eps) lastSpentDate = date;
    if (Math.abs(Number(pMap.get(date)?.earned_period) || 0) > eps) lastEarnedDate = date;
  }
  let actualCum = 0;
  let earnedCum = 0;
  let budgetCum = 0;
  return stopPerformanceLines(dates.map((date) => {
    const p = pMap.get(date);
    const t = tMap.get(date);
    const actual_period = Number(t?.actual_period) || 0;
    const earned_period = Number(p?.earned_period) || 0;
    const budget_period = Number(p?.budget_period) || 0;
    actualCum += actual_period;
    earnedCum += earned_period;
    budgetCum += budget_period;
    return {
      date,
      actual_period,
      actual_cumulative: lastSpentDate && date > lastSpentDate ? null : actualCum,
      shop_earned_period: Number(p?.shop_earned_period) || 0,
      field_earned_period: Number(p?.field_earned_period) || 0,
      earned_period,
      earned_cumulative: (p?.earned_cumulative == null || (lastEarnedDate && date > lastEarnedDate))
        ? null
        : earnedCum,
      budget_period,
      budget_cumulative: budgetCum,
      remaining_period: Number(p?.forecast_period || p?.remaining_period) || 0,
      remaining_cumulative: p?.forecast_cumulative == null && p?.remaining_cumulative == null
        ? null
        : Number(p.forecast_cumulative ?? p.remaining_cumulative)
    };
  }));
}
